import { adminApi, adminApiInZone, StaffApiError } from './api-client';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone } from './zones';

/**
 * Route one per-record call to the subscriber's own zone (ADR-122 Pattern 1).
 *
 * ⚠ OMITTING THE ZONE MEANS "HOME", NOT "ANY". An AccountUser lives in exactly one zone, so a
 * disable/enable/delete aimed at a us-east-1 subscriber from an AU-served console would 404 on
 * a row the operator is looking at. The list tags every row with `zone` so its actions can
 * pass it back.
 *
 * ⚠⚠ THE STAFF AND GEO-RESTRICTIONS TABS OF `UserManagementPage` MUST NOT USE ANY OF THIS.
 * They read the global `Staff` table, which ADR-120 Amendment 2 makes platform-wide and the
 * 21 September 2026 review proved absent from us-east-1 by DescribeTable. For those, "zone
 * doesn't apply" is the permanent, correct answer — fanning them out would invent a second
 * jurisdiction's staff list that does not exist.
 */
const call = <T>(zone: PortalZone | undefined, path: string, init?: RequestInit): Promise<T> =>
  zone ? adminApiInZone<T>(zone, path, init) : adminApi<T>(path, init);

/**
 * Typed wrappers around the admin-api subscriber routes.
 *
 *   - GET    /admin/subscribers                 list (Phase 5d-3b, enriched ADR-072)
 *   - POST   /admin/subscribers                 create Account User (ADR-072 D1)
 *   - GET    /admin/subscriber-nodes            node-attachment picker (ADR-072 item 3)
 *   - GET    /admin/subscribers/{sub}           drawer detail (ADR-072 D3)
 *   - PUT    /admin/subscribers/{sub}           rename / role-change (ADR-072 D3)
 *   - POST   /admin/subscribers/{sub}/disable | /enable | /resend
 *   - DELETE /admin/subscribers/{sub}           hard delete (typed-confirm)
 *   - POST   /admin/subscribers/{sub}/reset-password   support password (ADR-061)
 *
 * Server gate: MUTATING_ROLES (nexa_admin + super_admin); mutations also fresh-auth.
 * The Subscribers tab is super_admin-only at the surface level.
 */

// ── Subscriber Cedar roles (ADR-072 Decision 4 — supersedes legacy owner/member/
//    reseller). The six roles + node tier are the deployed subscriber-pool
//    authorization vocabulary (cedar/policies/subscriber-pool.cedar). ──────────

export const SUBSCRIBER_ROLES = [
  'entity_admin',
  'group_admin',
  'group_staff',
  'staff_view', // CEDAR-008 Amendment 1 (13 October 2026) — Staff (view only)
  'partner_admin',
  'partner_staff',
  'partner_view',
] as const;
export type SubscriberRole = (typeof SUBSCRIBER_ROLES)[number];

export type NodeType = 'entity' | 'group' | 'partner';

// ── ADR-112 D7 — management-tier operator rows (READ-ONLY on this surface) ────
//
// Deliberately NOT added to `SUBSCRIBER_ROLES` / `ROLES_BY_TIER` above. Those two
// drive what the picker OFFERS, and Dashboard Access (ADR-081 D11) is the single
// operator write path — surfacing operator roles as assignable here would
// re-create the second write path ADR-112 rules out. These constants exist only
// so an operator row can be DISPLAYED correctly (B9), never assigned.

// ── ADR-112 D3 — the orphan sweep (read-only) ────────────────────────────────

export interface OrphanedAccount {
  accountId: string;
  businessName: string;
  email: string;
  customerNumber: string;
  createdAt: string;
  setupState: string;
  /** False ⇒ not merely memberless but UNANCHORED (the ADR-112 backfill is owed). */
  hasSentinel: boolean;

  /**
   * ADR-122 Pattern 1 — which zone this orphaned account actually lives in.
   *
   * Added by the client after the fan-out, NOT returned by the server: each zone's API answers
   * only for itself and has no reason to name itself.
   */
  zone?: string;
}

export interface OrphanSweepIntegrity {
  accountsScanned: number;
  operatorOrgsSkipped: number;
  inFlightExcluded: number;
  missingSentinel: string[];
}

/** GET /admin/orphaned-accounts — customer accounts with no administering member.
 *  Read-only: remediation runs through Dashboard Access / the team routes (re-admin)
 *  or the ADR-079 delete cascade. */
export async function listOrphanedAccounts(
  /** The zone to sweep. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{
  orphaned: OrphanedAccount[];
  integrity: OrphanSweepIntegrity;
}> {
  return call<{ orphaned: OrphanedAccount[]; integrity: OrphanSweepIntegrity }>(zone,
    '/admin/orphaned-accounts',
  );
}

export interface OrphanedAccountsFanout {
  orphaned: OrphanedAccount[];
  integrity: OrphanSweepIntegrity;
  failedZones: string[];
}

/**
 * Orphaned accounts across every zone (ADR-122 Pattern 1).
 *
 * ⭐ AN ORPHAN IS A DATA-INTEGRITY FINDING, so a zone missing from this sweep does not just
 * shorten a list — it reports the platform as healthier than it is. The integrity block is
 * merged by CONCATENATING each zone's arrays: these are lists of specific offending ids, and
 * a union is the only merge that keeps each one identifiable.
 */
export async function listAllOrphanedAccounts(): Promise<OrphanedAccountsFanout> {
  const results = await fanOutZones(async (zone: PortalZone) => {
    const r = await listOrphanedAccounts(zone);
    return [{ zone, r }];
  });
  throwIfAllZonesFailed(results);
  const pages = results.flatMap((x) => x.items);

  // Merged FIELD BY FIELD rather than generically, because the three shapes here merge
  // differently and a generic reducer would silently pick one rule for all of them: the two
  // scan counters SUM (they count rows examined), while `missingSentinel` is a list of
  // specific offending account ids and must be a UNION — summing its length would report a
  // number with no ids behind it, which is the opposite of what an integrity finding is for.
  const integrity: OrphanSweepIntegrity = {
    accountsScanned: pages.reduce((n, { r }) => n + (r.integrity?.accountsScanned ?? 0), 0),
    operatorOrgsSkipped: pages.reduce((n, { r }) => n + (r.integrity?.operatorOrgsSkipped ?? 0), 0),
    inFlightExcluded: pages.reduce((n, { r }) => n + (r.integrity?.inFlightExcluded ?? 0), 0),
    missingSentinel: pages.flatMap(({ r }) => r.integrity?.missingSentinel ?? []),
  };

  return {
    orphaned: pages.flatMap(({ zone, r }) => r.orphaned.map((o) => ({ ...o, zone: zone.code }))),
    integrity,
    failedZones: failedZoneLabels(results),
  };
}

export const OPERATOR_ROLES = ['operator_admin', 'operator_staff'] as const;
export type OperatorRole = (typeof OPERATOR_ROLES)[number];

export const OPERATOR_ROLE_LABELS: Record<OperatorRole, string> = {
  operator_admin: 'Operator Admin',
  operator_staff: 'Operator Staff',
};

/** True iff this membership is an operator attachment. Checked on BOTH axes: a
 *  row malformed on one of them (the B3 rows are) must still read as operator. */
export function isOperatorMembership(m: { role?: string; nodeType?: string }): boolean {
  return m.nodeType === 'management'
    || (OPERATOR_ROLES as readonly string[]).includes(m.role ?? '');
}

/** Roles assignable at each node tier — the UI dropdown is filtered by this. */
export const ROLES_BY_TIER: Record<NodeType, SubscriberRole[]> = {
  entity: ['entity_admin', 'staff_view'],
  group: ['group_admin', 'group_staff', 'staff_view'],
  partner: ['partner_admin', 'partner_staff', 'partner_view'],
};

export const SUBSCRIBER_ROLE_LABELS: Record<SubscriberRole, string> = {
  entity_admin: 'Entity Admin',
  group_admin: 'Group Admin',
  group_staff: 'Group Staff',
  staff_view: 'Staff (view only)',
  partner_admin: 'Partner Admin',
  partner_staff: 'Partner Staff',
  partner_view: 'Partner Viewer',
};

/** One-line descriptions humanised from the Cedar policy semantics. */
export const SUBSCRIBER_ROLE_DESCRIPTIONS: Record<SubscriberRole, string> = {
  entity_admin: 'Full read/write on their own Entity’s resources, access, and billing — the account admin (ADR-088).',
  group_admin: 'Full read/write across every Entity in their Group; manage access and billing.',
  group_staff: 'Read/write across the Group and manage access at Entity level; no billing.',
  staff_view: 'Read-only across their own account (its Entity, or its Group); changes nothing.',
  partner_admin: 'Full read/write across the whole Partner subtree, plus billing and impersonation.',
  partner_staff: 'Manage clients across the Partner subtree; no billing or impersonation.',
  partner_view: 'Read-only across the whole Partner subtree.',
};

/**
 * Legacy labels for un-migrated rows (ADR-044) — display-only so old owner /
 * member / reseller rows render gracefully (ADR-072 migration coexistence). New /
 * edited rows always write a Cedar role.
 */
const LEGACY_ROLE_LABELS: Record<string, string> = {
  owner: 'Owner (legacy)',
  member: 'Member (legacy)',
  reseller: 'Reseller (legacy)',
  'group-admin': 'Group Admin (legacy)',
};

/**
 * Humanise a role for display.
 *
 * ⚠ B9 — the operator lookup is why this function exists in its current shape.
 * An `operator_admin` row previously fell through every map to the raw string in
 * the list badge, and in the drawer it was worse: the role `<select>` was
 * populated from the SUBSCRIBER roles, so a controlled value of `operator_admin`
 * matched no `<option>` and the browser rendered the FIRST option's label —
 * "Entity Admin". The row silently claimed a role it did not hold, and saving
 * from that state is what wrote the wrong role back (B1).
 */
export function subscriberRoleLabel(role: string): string {
  return (
    (SUBSCRIBER_ROLE_LABELS as Record<string, string>)[role] ??
    (OPERATOR_ROLE_LABELS as Record<string, string>)[role] ??
    LEGACY_ROLE_LABELS[role] ??
    (role || '—')
  );
}

export interface SubscriberListItem {
  cognitoSub: string;
  email: string;
  subscriberId: string;
  customerNumber: string;
  /** Legacy Cognito custom:role (stale post ADR-061) — prefer accountRole. */
  role: string;
  /** Authoritative Cedar role from the AccountUser row (ADR-072 enrichment). */
  accountRole?: string;
  nodeType?: string;
  nodeId?: string;
  accountId?: string;
  /** PL9 — the primary node's resolved human NAME (businessName / groupName /
   *  partnerName), server-side. Prefer this over a client-side node-list join;
   *  the raw nodeId is the secondary (subtitle) display. */
  nodeName?: string;
  /** PL13 — beta reflects membership in ANY beta ACCOUNT (Account.isBeta),
   *  derived server-side; NOT the per-user AccountUser row. Display only. */
  isBeta?: boolean;
  /** PL12 — ALL of this user's account memberships (a user can belong to 2+
   *  accounts). The row-level fields above carry the primary membership. */
  memberships?: SubscriberMembership[];
  /** Cognito UserStatus — e.g. CONFIRMED, FORCE_CHANGE_PASSWORD. */
  status: string;
  enabled: boolean;
  createdAt: string;

  /**
   * ADR-122 Pattern 1 — which zone this subscriber actually lives in.
   *
   * Added by the client after the fan-out, NOT returned by the server: each zone's API answers
   * only for itself and has no reason to name itself.
   */
  zone?: string;
}

/** PL12 — one of a subscriber's account memberships, with its node's resolved
 *  name (PL9) and account-derived beta flag (PL13). */
export interface SubscriberMembership {
  role: string;
  nodeType: string;
  nodeId: string;
  nodeName: string;
  accountId: string;
  isBeta: boolean;
}

/** The role to display for a row — the authoritative Cedar role if present. */
export function displayRole(s: SubscriberListItem): string {
  return s.accountRole || s.role || '';
}

export async function listSubscribers(
  search?: string,
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<SubscriberListItem[]> {
  const qs = search?.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
  const resp = await call<{ subscribers: SubscriberListItem[] }>(zone, `/admin/subscribers${qs}`);
  return resp.subscribers;
}

/**
 * Every subscriber, across every zone (ADR-122 Pattern 1).
 *
 * The fan-out runs in THIS BROWSER, one request per zone, each carrying the same globally-valid
 * staff token — see `lib/zones.ts` for why that is a compliance requirement and not a style
 * choice. With one zone configured this is exactly the single-zone read it replaced.
 */
export async function listAllSubscribers(
  search?: string,
): Promise<{ subscribers: SubscriberListItem[]; failedZones: string[] }> {
  const results = await fanOutZones(async (zone: PortalZone) =>
    (await listSubscribers(search, zone)).map((sub) => ({ ...sub, zone: zone.code })),
  );
  throwIfAllZonesFailed(results);
  return { subscribers: results.flatMap((r) => r.items), failedZones: failedZoneLabels(results) };
}

// ── Node-attachment picker (ADR-072 item 3) ─────────────────────────────────

/** ADR-081 operator-portal type — the badge vocabulary shared with the subscriber
 *  ContextSelectModal (PARTNER / BETA / CONSULTANT; never "Reseller"). */
export type OperatorPortalType = 'partner' | 'beta' | 'consultant';

export interface SubscriberNode {
  /** The Cedar ATTACHMENT TIER — drives ROLES_BY_TIER and is what the server
   *  validates on write. NOT a display type: operator orgs are stored as Account
   *  records, so they arrive here as `entity`. */
  nodeType: NodeType;
  nodeId: string;
  label: string;
  /** Present iff this node is an operator org rather than a customer account
   *  (ADR-081). Display-only — badges the picker so two same-named targets (the
   *  PARTNER org vs the ENTITY account) are distinguishable. */
  operatorPortalType?: OperatorPortalType;

  /**
   * ADR-122 Pattern 1 — which zone this node actually lives in.
   *
   * Added by the client after the fan-out, NOT returned by the server: each zone's API answers
   * only for itself and has no reason to name itself.
   */
  zone?: string;
}

/** The picker badge for a node: its true type, not the attachment tier. An
 *  operator org reads PARTNER / BETA / CONSULTANT; a customer account reads
 *  ENTITY. "Group" is deliberately absent from the operator vocabulary — it
 *  means a customer account with multiple domains. */
export function nodeBadgeLabel(node: SubscriberNode): string {
  return (node.operatorPortalType ?? node.nodeType).toUpperCase();
}

/**
 * Is this node an OPERATOR ORG rather than a customer account?
 *
 * ⭐ `operatorPortalType` IS THE ONLY AUTHORITY, and its ABSENCE is what means
 * "ordinary customer account" — the same rule `resolveAttachmentTarget` applies
 * server-side in `person-access-write.ts`. Operator orgs are stored as Account
 * records, so they arrive at the `entity` TIER; deriving this from `nodeType`
 * would classify every customer account as an operator org.
 *
 * ⚠ THIS IS A PRESENTATION PREDICATE, NOT A GUARD. The real refusal is the
 * server's `operator_org_not_addable` (ADR-112 D7) — the API is reachable
 * without the UI, so removing that because the picker no longer offers the path
 * would be a wrong-axis fix. This only decides what to OFFER.
 */
export function isOperatorOrgNode(node: SubscriberNode): boolean {
  return node.operatorPortalType !== undefined;
}

export async function listSubscriberNodes(
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<SubscriberNode[]> {
  const resp = await call<{ nodes: SubscriberNode[] }>(zone, '/admin/subscriber-nodes');
  return resp.nodes;
}

/**
 * Every attachable node, across every zone (ADR-122 Pattern 1).
 *
 * ⭐ THIS IS WHAT MAKES TRACK 7's "CREATE IN ZONE" POSSIBLE AT ALL. A new subscriber is
 * attached to a node, and a node lives in exactly one zone — so a node picker that only ever
 * listed the home zone's nodes could not express "create this person against a US account",
 * however the target zone was chosen. The node IS the zone choice.
 */
export async function listAllSubscriberNodes(): Promise<{
  nodes: SubscriberNode[];
  failedZones: string[];
}> {
  const results = await fanOutZones(async (zone: PortalZone) =>
    (await listSubscriberNodes(zone)).map((n) => ({ ...n, zone: zone.code })),
  );
  throwIfAllZonesFailed(results);
  return { nodes: results.flatMap((r) => r.items), failedZones: failedZoneLabels(results) };
}

// ── Create (ADR-072 D1) ─────────────────────────────────────────────────────

export interface CreateSubscriberInput {
  email: string;
  displayName?: string;
  nodeType: NodeType;
  nodeId: string;
  role: SubscriberRole;
}

/**
 * ADR-122 Track 7 — create a subscriber IN A CHOSEN ZONE.
 *
 * ⭐⭐ THIS IS THE ONE CALL IN THE WHOLE BRIEF THAT CHANGES WHERE A RECORD IS BORN, rather than
 * where an existing one is read. Everything else routes to a zone a record already has; this
 * decides it. The zone is not a free choice bolted on top: it is `input.nodeId`'s zone, since
 * the new subscriber is attached to a node and a node lives in exactly one place. Passing a
 * zone that disagrees with the node's would simply 404 on the node.
 *
 * Server-side, the handler's `recordIdentityZone(newEmail)` writes `THIS_ZONE` unconditionally
 * — there is no zone argument to pass it — so the zone of the account created is, correctly,
 * the zone of whichever Lambda answers. Choosing which Lambda answers IS therefore the whole
 * mechanism, and it is what this parameter does.
 *
 * Before this, a manually-created subscriber always landed in whichever zone the staff console
 * itself was talking to (home/AU), which is why the test plan lists "get a test account into
 * us-east-1" as a workaround rather than a capability.
 */
export function createSubscriber(
  input: CreateSubscriberInput,
  /** The zone to create in — the node's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ created: boolean; cognitoSub: string; email: string; role: string; nodeType: string; nodeId: string }> {
  return call(zone, '/admin/subscribers', {
    method: 'POST',
    body: JSON.stringify({
      email: input.email,
      ...(input.displayName ? { displayName: input.displayName } : {}),
      nodeType: input.nodeType,
      nodeId: input.nodeId,
      role: input.role,
    }),
  });
}

// ── Drawer detail + lifecycle (ADR-072 D3) ──────────────────────────────────

export interface SubscriberAccountRow {
  accountId: string;
  role: string;
  nodeType?: string;
  nodeId?: string;
  /** PL9 — resolved node NAME (businessName / groupName / partnerName). */
  nodeName?: string;
  /** PL13 — this membership's ACCOUNT beta flag (Account.isBeta). */
  isBeta?: boolean;
  grantedAt: string;
  grantedBy: string;
}

export interface SubscriberAuditEntry {
  action: string;
  performedBy: string;
  at: string;
  detail?: string;
  /** True for an entry SYNTHESISED from a membership row's `grantedAt` rather
   *  than read from the admin-audit trail. See `SUBSCRIBER_AUDIT_ACTION_LABELS`. */
  derived?: boolean;
}

/**
 * Human labels for the actions the drilldown's Activity list can show.
 *
 * ⭐ WHY THIS EXISTS NOW AND DID NOT BEFORE. The list could previously only ever
 * render `account_user_granted` (the server synthesised that one string and read
 * no trail at all — UAT fail 1a), so a raw snake_case code was survivable. It now
 * renders operator suspends, reactivations, removals, appointments, consultant
 * assignments and revocations, and a column of raw identifiers is not an activity
 * trail a person can read. Mirrors `STAFF_AUDIT_ACTION_LABELS`.
 *
 * ⚠ THE FALL-THROUGH IS A HUMANISED CODE, NEVER A BLANK. An action added
 * server-side and not listed here must still render as something — see
 * `subscriberAuditActionLabel`. A missing label is a display gap, not a reason to
 * hide the event, because hiding it reproduces exactly the defect this fixes.
 */
export const SUBSCRIBER_AUDIT_ACTION_LABELS: Record<string, string> = {
  account_user_created: 'Account User created',
  account_user_granted: 'Membership granted',
  account_user_role_changed: 'Membership role changed',
  account_user_revoked: 'Membership revoked',
  account_user_disabled: 'Sign-in disabled',
  account_user_enabled: 'Sign-in re-enabled',
  portal_operator_added: 'Operator attachment added',
  portal_operator_suspend: 'Operator attachment suspended',
  portal_operator_reactivate: 'Operator attachment reactivated',
  portal_operator_removed: 'Operator attachment removed',
  portal_operator_set_role: 'Operator role changed',
  portal_operator_set_scope: 'Operator scope changed',
  portal_operator_set_visibility: 'Operator visibility changed',
  consultant_assigned: 'Assigned to a subscriber',
  consultant_unassigned: 'Unassigned from a subscriber',
  advocate_member_removed: 'Advocate membership removed',
};

/** A readable label for an audit action, mapped or humanised. */
export function subscriberAuditActionLabel(action: string): string {
  const mapped = SUBSCRIBER_AUDIT_ACTION_LABELS[action];
  if (mapped) return mapped;
  if (!action) return 'Activity';
  // Humanise rather than drop: `some_new_action` → `Some new action`.
  const words = action.replace(/[_:]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface SubscriberDetail {
  cognitoSub: string;
  email: string;
  displayName: string;
  subscriberId: string;
  customerNumber: string;
  status: string;
  enabled: boolean;
  role: string;
  nodeType: string;
  nodeId: string;
  /** PL9 — the primary node's resolved NAME (header primary; nodeId secondary). */
  nodeName?: string;
  accountId: string;
  /** PL13 — beta reflects membership in ANY beta account (account-derived). */
  isBeta?: boolean;
}

export function getSubscriberDetail(
  cognitoSub: string,
  /** The subscriber's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{
  subscriber: SubscriberDetail;
  accounts: SubscriberAccountRow[];
  audit: SubscriberAuditEntry[];
  /** The audit trail could not be read. ⚠ NOT the same as an empty trail — the
   *  drawer must say so rather than render "no activity" (UAT fail 1a). */
  auditUnavailable?: boolean;
}> {
  return call(zone, `/admin/subscribers/${encodeURIComponent(cognitoSub)}`);
}

/**
 * ADR-123 Stage 3b — which zone(s) hold this subscriber, for a deep link that does not say.
 *
 * ⭐ A 404 FROM A ZONE MEANS "NOT HERE", ANY OTHER FAILURE MEANS "DID NOT ANSWER", and the two
 * must not be merged: a zone that was down cannot be reported as not holding the person, or a
 * US outage would render as "this subscriber does not exist". So a 404 contributes no match and
 * no failure; every other error is a failed zone, named to the operator.
 */
export async function locateSubscriber(cognitoSub: string): Promise<{
  found: PortalZone[];
  failedZones: string[];
}> {
  const results = await fanOutZones(async (zone: PortalZone) => {
    try {
      await getSubscriberDetail(cognitoSub, zone);
      return [zone];
    } catch (err) {
      if (err instanceof StaffApiError && err.status === 404) return [];
      throw err;
    }
  });
  return { found: results.flatMap((r) => r.items), failedZones: failedZoneLabels(results) };
}

export function updateSubscriberName(
  cognitoSub: string,
  displayName: string,
  /** The subscriber's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<unknown> {
  return call(zone, `/admin/subscribers/${encodeURIComponent(cognitoSub)}`, {
    method: 'PUT',
    body: JSON.stringify({ displayName }),
  });
}

/**
 * ⚠ B51 — PREFER `updateMembership` BELOW. A role edit belongs on the
 * per-membership route, which names its target account in the PATH. This legacy
 * PUT now REFUSES (400 `ambiguous_membership_target`) when the subscriber holds
 * more than one membership and no `currentAccountId` is supplied — it used to
 * silently edit, and on a tier crossing DELETE, whichever membership came first.
 * Pass `currentAccountId` if you must use this; the UI does not.
 */
export function updateSubscriberRole(
  cognitoSub: string,
  role: SubscriberRole,
  nodeType: NodeType,
  nodeId: string,
  currentAccountId?: string,
  /** The subscriber's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<unknown> {
  return call(zone, `/admin/subscribers/${encodeURIComponent(cognitoSub)}`, {
    method: 'PUT',
    body: JSON.stringify({ role, nodeType, nodeId, ...(currentAccountId ? { currentAccountId } : {}) }),
  });
}

/**
 * Combined update (PL7) — commit a display-name change AND/OR a role/node
 * re-attachment in a SINGLE PUT. The server's updateSubscriber handler already
 * accepts displayName + role + nodeType + nodeId together and applies only the
 * fields present, so one call covers both without the two clobbering each other.
 * Callers pass only the fields that changed; role/nodeType/nodeId travel as a
 * set (a role change is always a node re-attachment — never a silent swap).
 */
export interface UpdateSubscriberInput {
  displayName?: string;
  role?: SubscriberRole;
  nodeType?: NodeType;
  nodeId?: string;
  /** B51 — WHICH membership the role change targets. Required whenever the
   *  subscriber holds more than one; omitting it is a 400, not a guess. */
  currentAccountId?: string;
}

export function updateSubscriber(
  cognitoSub: string,
  input: UpdateSubscriberInput,
  /** The subscriber's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<unknown> {
  return call(zone, `/admin/subscribers/${encodeURIComponent(cognitoSub)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// ADR-081 (ADR-098 Part B) — per-membership Account-User editing. Each grant at
// a node is its own editable unit: add / change-role / revoke — replacing the
// single primary-only PUT above for the membership case.
// ─────────────────────────────────────────────────────────────────────────

export interface MembershipView {
  accountId: string;
  role: string;
  nodeType: string;
  nodeId: string;
  nodeName: string;
  isBeta: boolean;
}

/** Grant a new membership at a node (POST /admin/subscribers/{sub}/memberships). */
export function addMembership(
  cognitoSub: string,
  input: { nodeType: NodeType; nodeId: string; role: SubscriberRole },
  /** The NODE'S zone — where the membership row has to land. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ membership: MembershipView }> {
  return call(zone, `/admin/subscribers/${encodeURIComponent(cognitoSub)}/memberships`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Change ONE membership's role (PATCH …/memberships/{accountId}). */
export function updateMembership(
  cognitoSub: string,
  accountId: string,
  role: SubscriberRole,
  /** The ACCOUNT'S zone — where this membership lives. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ membership: MembershipView }> {
  return call(zone,
    `/admin/subscribers/${encodeURIComponent(cognitoSub)}/memberships/${encodeURIComponent(accountId)}`,
    { method: 'PATCH', body: JSON.stringify({ role }) },
  );
}

/** Revoke ONE membership (DELETE …/memberships/{accountId}). */
export function removeMembership(
  cognitoSub: string,
  accountId: string,
  /** The ACCOUNT'S zone — where this membership lives. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ revoked: boolean }> {
  return call(zone,
    `/admin/subscribers/${encodeURIComponent(cognitoSub)}/memberships/${encodeURIComponent(accountId)}`,
    { method: 'DELETE' },
  );
}

export function disableSubscriber(
  cognitoSub: string,
  reason?: string,
  /** The subscriber's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<unknown> {
  return call(zone, `/admin/subscribers/${encodeURIComponent(cognitoSub)}/disable`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

export function enableSubscriber(
  cognitoSub: string,
  /** The subscriber's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<unknown> {
  return call(zone, `/admin/subscribers/${encodeURIComponent(cognitoSub)}/enable`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function resendSubscriberInvite(
  cognitoSub: string,
  /** The subscriber's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ resent: boolean; email: string }> {
  return call(zone, `/admin/subscribers/${encodeURIComponent(cognitoSub)}/resend`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function deleteSubscriber(
  cognitoSub: string,
  confirmEmail: string,
  /**
   * The subscriber's own zone. Omitted = the home zone.
   *
   * ⚠ IRREVERSIBLE, SO THE ZONE MATTERS MOST HERE — a delete aimed at the wrong jurisdiction
   * is a 404 the operator reads as "already gone", or nothing at all while they believe the
   * identity was removed.
   */
  zone?: PortalZone,
): Promise<{ deleted: boolean }> {
  return call(zone, `/admin/subscribers/${encodeURIComponent(cognitoSub)}`, {
    method: 'DELETE',
    body: JSON.stringify({ confirm: confirmEmail }),
  });
}

// ── Support password reset (ADR-061) ────────────────────────────────────────

export interface SupportPasswordReset {
  /** One-time phoneable password (e.g. KRND-4789-mkph). Shown once. */
  password: string;
  /** Epoch seconds at which the temp password stops working. */
  expiresAt: number;
  expiresInMinutes: number;
}

export async function resetSubscriberPassword(
  cognitoSub: string,
  /** The subscriber's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<SupportPasswordReset> {
  return call<SupportPasswordReset>(zone,
    `/admin/subscribers/${encodeURIComponent(cognitoSub)}/reset-password`,
    { method: 'POST' },
  );
}
